import React, { useContext, useReducer, useCallback } from 'react';
import { LaboratoryDocumentsContext } from './LaboratoryDocumentsContext';
import { AxiosContext } from '../AxiosContext';
import { Context } from '../Context';
import {
  laboratoryDocumentsReducer,
  INITIAL_STATE,
} from './laboratoryDocumentsReducer';
import {
  recalculateSummary,
} from '../../helpers/laboratoryDocuments/statusHelpers';
import {
  transformFlatDataToDocuments,
} from '../../helpers/laboratoryDocuments/transformers';

export const LaboratoryDocumentsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(laboratoryDocumentsReducer, INITIAL_STATE);
  const { userName } = useContext(Context);
  const axiosContext = useContext(AxiosContext);

  // ---- Paso 1: Upload PDFs ----
  const uploadPdfs = useCallback(
    async (files) => {
      dispatch({ type: 'SET_UPLOADING', payload: true });
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        const formData = new FormData();
        formData.append('username', userName);
        files.forEach((fileItem, index) => {
          formData.append(`pdf_${index}`, fileItem.file);
        });

        const response = await axiosContext.authAxios.post(
          'v1/laboratorydocument/upload-pdfs',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: percentCompleted });
            },
          }
        );

        const { data } = response;

        if (data.firebaseStorageUrl) {
          dispatch({ type: 'SET_FIREBASE_URLS', payload: data.firebaseStorageUrl });
        }

        // Transformar la respuesta plana del backend en estructura jerárquica
        const nestedDocuments = transformFlatDataToDocuments(data);

        if (nestedDocuments.length > 0) {
          dispatch({ type: 'SET_DOCUMENTS', payload: nestedDocuments });

          const summary = recalculateSummary(nestedDocuments);
          dispatch({ type: 'SET_SUMMARY', payload: summary });
        }

        dispatch({
          type: 'SET_STEP_COMPLETED',
          payload: { step: 0, completed: true },
        });

        dispatch({ type: 'SET_ACTIVE_STEP', payload: 1 });

        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message: 'PDFs procesados correctamente',
            severity: 'success',
          },
        });

        return data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Error al procesar los PDFs';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        dispatch({
          type: 'SET_SNACKBAR',
          payload: { message: errorMessage, severity: 'error' },
        });
        throw error;
      } finally {
        dispatch({ type: 'SET_UPLOADING', payload: false });
      }
    },
    [axiosContext]
  );

  // ---- Paso 3: Guardar documentos corregidos ----
  const saveDocuments = useCallback(
    async (documentsData) => {
      dispatch({ type: 'SET_SAVING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        const payload = {
          input: {
            username: userName,
            data: documentsData || state.documents,
          },
        };

        const response = await axiosContext.authAxios.post(
          'v1/laboratorydocument/save-laboratory-documents',
          payload
        );

        dispatch({
          type: 'SET_STEP_COMPLETED',
          payload: { step: 2, completed: true },
        });

        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message: 'Documentos guardados correctamente',
            severity: 'success',
          },
        });

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Error al guardar los documentos';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        dispatch({
          type: 'SET_SNACKBAR',
          payload: { message: errorMessage, severity: 'error' },
        });
        throw error;
      } finally {
        dispatch({ type: 'SET_SAVING', payload: false });
      }
    },
    [axiosContext, userName, state.documents]
  );

  // ---- Pestaña 2: Listar documentos ----
  const listDocuments = useCallback(
    async (filters = {}, pagination = {}, sort = {}) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const body = {
          filter: filters,
          limit: pagination.pageSize || 25,
          page: pagination.page || 1,
          sort: sort,
        };

        const response = await axiosContext.authAxios.post(
          'v1/laboratorydocument/list-laboratory-documents',
          body
        );

        dispatch({
          type: 'SET_VISUALIZATION_DOCUMENTS',
          payload: response.data?.documents || [],
        });

        return response.data;
      } catch (error) {
        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message: 'Error al obtener los documentos',
            severity: 'error',
          },
        });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [axiosContext]
  );

  // ---- Pestaña 2: Contar documentos ----
  const listDocumentsCount = useCallback(
    async (filters = {}) => {
      try {
        const body = { filter: filters };
        const response = await axiosContext.authAxios.post(
          'v1/laboratorydocument/list-laboratory-documents-count',
          body
        );

        dispatch({
          type: 'SET_VISUALIZATION_COUNT',
          payload: response.data?.count || 0,
        });

        return response.data;
      } catch (error) {
        return { count: 0 };
      }
    },
    [axiosContext]
  );

  // ---- Pestaña 2: Obtener documento individual ----
  const getDocument = useCallback(
    async (id) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const response = await axiosContext.authAxios.get(
          `v1/laboratorydocument/get-laboratory-document/${id}`
        );
        return response.data;
      } catch (error) {
        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message: 'Error al obtener el documento',
            severity: 'error',
          },
        });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [axiosContext]
  );

  // ---- Pestaña 2: Eliminar documento ----
  const deleteDocument = useCallback(
    async (documentId) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        await axiosContext.authAxios.post(
          'v1/laboratorydocument/delete-laboratory-document',
          { documentId }
        );

        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message: 'Documento eliminado correctamente',
            severity: 'success',
          },
        });

        return true;
      } catch (error) {
        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message: 'Error al eliminar el documento',
            severity: 'error',
          },
        });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [axiosContext]
  );

  return (
    <LaboratoryDocumentsContext.Provider
      value={{
        state,
        dispatch,
        uploadPdfs,
        saveDocuments,
        listDocuments,
        listDocumentsCount,
        getDocument,
        deleteDocument,
      }}
    >
      {children}
    </LaboratoryDocumentsContext.Provider>
  );
};
